/* IMPORTS */
import React, { useEffect, useState } from "react";
import { Correlation } from "./Correlation";
import { CartesianGrid, ComposedChart, Line, Scatter, XAxis, YAxis } from "recharts";
import { Button, Pagination } from "react-bootstrap";
import gettext from "../../../shared/utils/gettext";
import {
  BestCorTypes,
  CorDataTypes,
  CorDetailsItemTypes,
  CorDetailsTypes,
} from "./ObjectInterfaces";

/* Assigning the prop types */
interface ViewCorrelationsProps {
  correlations: BestCorTypes[];
  getDisplayName: any;
  measurementDetails: CorDetailsTypes;
  visualizeCorrelation: any;
}

/* Local type interfaces */
interface CorKeysTypes {
  key1: string;
  key2: string;
}

/* ViewCorrelations Component */
export const ViewCorrelations = ({
  correlations,
  getDisplayName,
  measurementDetails,
  visualizeCorrelation,
}: ViewCorrelationsProps) => {
  const [activePageNumber, setActivePageNumber] = useState<number>(1);
  const [cor, setCor] = useState<BestCorTypes>(correlations[0]);
  const [corData, setCorData] = useState<CorDataTypes>(cor.regular);
  const [corKeys, setCorKeys] = useState<CorKeysTypes>({
    key1: correlations[0].regular.var1Key,
    key2: correlations[0].regular.var2Key,
  });
  const [flipped, setFlipped] = useState<boolean>(false);
  const [var1Details, setVar1Details] = useState<CorDetailsItemTypes>(
    measurementDetails[corData.var1Key]
  );
  const [var2Details, setVar2Details] = useState<CorDetailsItemTypes>(
    measurementDetails[corData.var2Key]
  );

  /* Hook keeping track of, and displaying the selected correlation */
  useEffect(() => {
    // TODO use correlations.find
    for (let i: number = 0; i < correlations.length; i++) {
      if (
        correlations[i].regular.var1Key === corKeys.key1 &&
        correlations[i].regular.var2Key === corKeys.key2
      ) {
        setCor(correlations[i]);
        setFlipped(false);
        return;
      }
    }
  }, [corKeys, correlations]);

  /* Hook keeping track of the orientation of the correlation, x ~ y or y ~ x */
  useEffect(() => {
    setCorData(flipped ? cor.flipped : cor.regular);
  }, [cor, flipped]);

  /* Hook updating the selected details when corData changes */
  useEffect(() => {
    setVar1Details(measurementDetails[corData.var1Key]);
    setVar2Details(measurementDetails[corData.var2Key]);
  }, [corData, measurementDetails]);

  /* Column storage */
  const columns: [string, string | JSX.Element][][][] = [];

  /* Creating pagination based on the amount of correlations, with 5 on each page */
  let pages: JSX.Element[] = [];
  for (let number: number = 1; number <= Math.ceil(correlations.length / 5); number++) {
    pages.push(
      <Pagination.Item
        key={number}
        active={number === activePageNumber}
        onClick={(event: any) => {
          let page: string = event.target.text;
          if (!isNaN(parseInt(page))) {
            setActivePageNumber(parseInt(page));
          }
        }}
      >
        {number}
      </Pagination.Item>
    );
  }

  const pagination: JSX.Element = (
    <div>
      <Pagination size="sm">{pages}</Pagination>
    </div>
  );

  /* Assigns the correlations to a page and updates the shown correlations upon changing pages */
  const corPages: [string, string | JSX.Element][][][] = [];
  let col: [string, string | JSX.Element][][] = [[["", gettext("The best correlations:")]]];
  for (let i: number = 0; i < correlations.length; i++) {
    if (
      correlations[i].regular.var1Key === corKeys.key1 &&
      correlations[i].regular.var2Key === corKeys.key2
    ) {
      col.push([
        [
          "",
          <Correlation
            key={i}
            correlation={correlations[i]}
            measurementDetails={measurementDetails}
            getDisplayName={getDisplayName}
            setCorKeys={setCorKeys}
            selected={true}
          />,
        ],
      ]);
    } else {
      col.push([
        [
          "",
          <Correlation
            key={i}
            correlation={correlations[i]}
            measurementDetails={measurementDetails}
            getDisplayName={getDisplayName}
            setCorKeys={setCorKeys}
            selected={false}
          />,
        ],
      ]);
    }
    if ((i + 1) % 5 === 0 || i + 1 === correlations.length) {
      if (correlations.length > 5) {
        col.push([[gettext("More Correlations"), pagination]]);
      }

      corPages.push(col);
      col = [[["", gettext("Correlations")]]];
    }
  }
  columns.push(corPages[activePageNumber - 1]);

  /* Scatter plot for the two measurements of a correlation, with the regression line drawn through */
  const plot: JSX.Element = (
    <ComposedChart
      width={430}
      height={330}
      data={corData.dataPoints.points}
      margin={{
        top: 15,
        right: 100,
        bottom: 20,
        left: 50,
      }}
    >
      <CartesianGrid stroke="#f5f5f5" />
      <XAxis
        dataKey={corData.var1Key}
        type="number"
        // @ts-ignore   [Proper TS Solution Needed]
        label={{
          value: getDisplayName(var1Details.code, var1Details.name),
          position: "insideBottom",
          offset: 0,
        }}
        domain={[corData.dataPoints.xMin, corData.dataPoints.xMax]}
        allowDataOverflow={true}
      />
      <YAxis
        type="number"
        // @ts-ignore   [Proper TS Solution Needed]
        label={{
          value: getDisplayName(var2Details.code, var2Details.name),
          position: "insideBottomLeft",
          angle: -90,
          offset: -15,
        }}
        domain={[corData.dataPoints.yMin, corData.dataPoints.yMax]}
        allowDataOverflow={true}
      />
      <Scatter className={corData.key} dataKey={corData.var2Key} fill="blue" />
      <Line dataKey="regLine" stroke="red" dot={false} activeDot={false} strokeWidth={4} />
    </ComposedChart>
  );

  /* Flip Button */
  const flipButton: JSX.Element = (
    <Button
      onClick={() => setFlipped(!flipped)}
      style={{ marginTop: "5px" }}
      className="btn btn-primary btn-lg"
    >
      {gettext("Flip")}
    </Button>
  );

  /* Visualize Button */
  const visualizeButton: JSX.Element = (
    <Button
      onClick={() => visualizeCorrelation(var1Details, var2Details)}
      style={{ marginTop: "5px" }}
      className="btn btn-primary btn-lg"
    >
      {gettext("Visualize")}
    </Button>
  );

  /* Building settings menu / column */
  const resultCol: [string, string | JSX.Element][][] = [
    [
      [
        "",
        `${getDisplayName(var1Details.code, var1Details.name)} ~ ${getDisplayName(
          var2Details.code,
          var2Details.name
        )}`,
      ],
    ],
    [
      [gettext("Dataset 1"), var1Details.dataset],
      [gettext("Measurement 1"), getDisplayName(var1Details.code, var1Details.name)],
      [gettext("Date 1"), var1Details.date],
    ],
    [
      [gettext("Dataset 2"), var2Details.dataset],
      [gettext("Measurement 2"), getDisplayName(var2Details.code, var2Details.name)],
      [gettext("Date 2"), measurementDetails[corData.var2Key].date],
    ],
    [
      [gettext("Correlation"), "" + corData.cor + ""],
      [gettext("Regression R2"), "" + corData.r2 + ""],
    ],
    [
      [gettext("Plot"), plot],
      ["", ""],
    ],
    [
      [gettext("Flip Correlation"), flipButton],
      ["Visualize Correlation", visualizeButton],
    ],
  ];
  columns.push(resultCol);

  /* JSX */
  return (
    <div>
      <div className="row">
        {columns.map((col: [string, string | JSX.Element][][], i: number) => (
          <div className="col p-3 border-right" key={i}>
            {i === 0 ? (
              col.map((row: [string, string | JSX.Element][], j: number) =>
                row.map((item: [string, string | JSX.Element]) => (
                  <div className="fieldset pb-2" key={j}>
                    <label className="text-muted mb-0">{item[0]}</label>
                    <div className={"text header" + (j === 0 ? " font-weight-bold" : "")}>
                      {item[1]}
                    </div>
                  </div>
                ))
              )
            ) : (
              <div className="bg-white">
                {col.map((row: [string, string | JSX.Element][], i: number) => (
                  <>
                    {row.length > 1 ? (
                      <table key={i}>
                        <tbody>
                          <tr>
                            {row.map((item: [string, string | JSX.Element], k: number) => (
                              <td
                                key={k}
                                style={{ width: "260px", height: "40px", verticalAlign: "top" }}
                              >
                                <div className="fieldset pb-2" key={k}>
                                  <label className="text-muted mb-0">{item[0]}</label>
                                  <div
                                    className={"text header" + (i === 0 ? " font-weight-bold" : "")}
                                  >
                                    {item[1]}
                                  </div>
                                </div>
                              </td>
                            ))}
                          </tr>
                        </tbody>
                      </table>
                    ) : (
                      <div className="fieldset pb-2" key={i}>
                        <label className="text-muted mb-0">{row[0][0]}</label>
                        <div className={"text header" + (i === 0 ? " font-weight-bold" : "")}>
                          {row[0][1]}
                        </div>
                      </div>
                    )}
                  </>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
